import { Services, ServicesLabels, StatusVariant } from '@consigli/types';
import { FC, useMemo } from 'react';

import { Badge, Combobox, Field, Fieldset } from '@/components';

import { EditProjectFormData, FormErrors } from './types';

interface AvailableServicesFieldsProps {
  formData: EditProjectFormData;
  errors: FormErrors;
  onFieldChange: (field: keyof EditProjectFormData, value: unknown) => void;
  orgServices: Services[];
  usedExternalServices: Services[];
}

export const AvailableServicesFields: FC<AvailableServicesFieldsProps> = ({
  formData,
  errors,
  onFieldChange,
  orgServices,
  usedExternalServices,
}) => {
  // Only show services that are NOT already enabled at the organization level
  const overrideServiceOptions = useMemo(
    () =>
      Object.values(Services)
        .filter((service) => !orgServices.includes(service))
        .map((service) => ({
          value: service,
          label: ServicesLabels[service],
        })),
    [orgServices],
  );

  const handleServicesChange = (selectedServices: string[]) => {
    onFieldChange('availableServicesOverride', selectedServices as Services[]);
  };

  return (
    <div className="space-y-6">
      <Fieldset legend="Organization Services">
        {orgServices.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {orgServices.map((service) => (
              <Badge key={service} text={ServicesLabels[service]} variant={StatusVariant.SUCCESS} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No organization services configured.</p>
        )}
      </Fieldset>

      <Fieldset legend="Used External Services">
        {usedExternalServices.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {usedExternalServices.map((service) => (
              <Badge
                key={service}
                text={ServicesLabels[service] ?? service}
                variant={StatusVariant.SUCCESS}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No external services used yet.</p>
        )}
        <p className="text-sm text-muted-foreground mt-2">
          Apps outside monoweb that have been used on this project, which shows them as applied. Set
          by the app itself, not here.
        </p>
      </Fieldset>

      {overrideServiceOptions.length > 0 && (
        <Fieldset legend="Additional Services">
          <Field
            label="Enable Additional Services"
            htmlFor="services-override-combobox"
            error={errors.availableServicesOverride}
          >
            <Combobox
              optionGroups={[
                {
                  label: 'Services',
                  options: overrideServiceOptions,
                },
              ]}
              fallbackLabel="No additional services available"
              placeholder="Select additional services..."
              value={formData.availableServicesOverride}
              onValueChange={handleServicesChange}
              className="w-full"
              search={true}
              searchPlaceholder="Search services..."
            />
          </Field>
          <p className="text-sm text-muted-foreground mt-2">
            Select services to enable for this project beyond the organization&apos;s default
            services.
          </p>
        </Fieldset>
      )}
    </div>
  );
};
